import { useCallback, useEffect, useRef, useState } from 'react'
import { Search, X, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { webhookDeliveryAttemptApi } from '@/api/services'
import { useToast } from '@/context/ToastContext'
import { Button } from '@/components/ui/Button'
import { Badge, Spinner, EmptyState, Label } from '@/components/ui/misc'
import { cn } from '@/lib/utils'
import { toastApiError } from '@/lib/apiError'
import { formatWebhookResponse } from '@/lib/webhookResponse'
import type { WebhookDeliveryAttempt } from '@/types'

const PAGE_SIZE = 15

type OutcomeFilter = 'all' | 'ok' | 'failed'

export default function WebhookDeliveryAttemptsPage() {
  const { toast } = useToast()

  const [items, setItems] = useState<WebhookDeliveryAttempt[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [outcome, setOutcome] = useState<OutcomeFilter>('all')
  const [loading, setLoading] = useState(true)
  const [retrying, setRetrying] = useState(false)
  const [selectedFailedIds, setSelectedFailedIds] = useState<Set<string>>(new Set())

  const searchTimer = useRef<ReturnType<typeof setTimeout>>()
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 350)
    return () => clearTimeout(searchTimer.current)
  }, [search])

  const successParam = outcome === 'all' ? undefined : outcome === 'ok'

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await webhookDeliveryAttemptApi.getAll({
        filter: debouncedSearch || undefined,
        success: successParam,
        pageNumber: page,
        pageSize: PAGE_SIZE,
      })
      setItems(res.items)
      setTotalCount(res.totalCount)
      setTotalPages(res.totalPages)
      setSelectedFailedIds(new Set())
    } catch (e: unknown) {
      toastApiError(toast, e, 'Failed to load delivery attempts')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, page, successParam, toast])

  useEffect(() => { load() }, [load])

  function toggleRow(id: string, success: boolean) {
    if (success) return
    setSelectedFailedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAllFailedOnPage() {
    const failed = items.filter(i => !i.success).map(i => i.id)
    setSelectedFailedIds(new Set(failed))
  }

  async function retrySelected() {
    if (selectedFailedIds.size === 0) return
    setRetrying(true)
    try {
      const batch = await webhookDeliveryAttemptApi.retryFailed([...selectedFailedIds])
      const ok = batch.items.filter(i => i.ok).length
      const bad = batch.items.length - ok
      toast(
        bad === 0
          ? `Retried ${ok} delivery attempt${ok !== 1 ? 's' : ''}.`
          : `Retried: ${ok} succeeded, ${bad} still failed. Refresh the list for new rows.`,
        bad === 0 ? 'success' : 'error',
      )
      await load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Retry failed')
    } finally {
      setRetrying(false)
    }
  }

  function clearSearch() {
    setSearch('')
    searchRef.current?.focus()
  }

  const from = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const to = Math.min(page * PAGE_SIZE, totalCount)
  const failedOnPage = items.filter(i => !i.success)

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex flex-wrap items-center gap-4 border-b border-border px-6 py-4 bg-surface flex-shrink-0">
        <div className="min-w-0">
          <h1 className="text-sm font-black tracking-wider text-ink">WEBHOOK DELIVERIES</h1>
          <p className="text-xs text-muted font-mono mt-0.5">
            {loading ? 'Loading…' : `${totalCount} attempt${totalCount !== 1 ? 's' : ''}`}
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3 flex-1 min-w-[200px]">
          <div className="flex-1 min-w-[180px] max-w-sm relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted pointer-events-none" />
            <input
              ref={searchRef}
              className="w-full rounded-md border border-border2 bg-bg pl-8 pr-8 py-1.5 text-xs font-mono text-ink placeholder:text-muted focus:border-accent focus:outline-none transition-colors"
              placeholder="Search tenant, URL, response, payload…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button type="button" onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-ink transition-colors">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <div>
            <Label className="mb-1 block">Outcome</Label>
            <select
              className="rounded-md border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
              value={outcome}
              onChange={e => {
                setOutcome(e.target.value as OutcomeFilter)
                setPage(1)
              }}
            >
              <option value="all">All</option>
              <option value="ok">Success only</option>
              <option value="failed">Failed only</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {failedOnPage.length > 0 && (
            <Button variant="ghost" size="sm" onClick={selectAllFailedOnPage} type="button">
              Select failed ({failedOnPage.length})
            </Button>
          )}
          <Button
            onClick={retrySelected}
            disabled={selectedFailedIds.size === 0 || retrying}
            type="button"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', retrying && 'animate-spin')} />
            Retry selected{selectedFailedIds.size > 0 ? ` (${selectedFailedIds.size})` : ''}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex justify-center py-20"><Spinner className="h-6 w-6" /></div>
        ) : items.length === 0 ? (
          <EmptyState
            message={debouncedSearch || outcome !== 'all'
              ? 'No delivery attempts match your filters.'
              : 'No webhook deliveries recorded yet. They appear when templates are published to subscribers.'}
          />
        ) : (
          <div className="rounded-lg border border-border overflow-hidden bg-surface">
            <table className="w-full text-left text-xs">
              <thead className="bg-border/40 font-mono text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="w-10 px-3 py-2" aria-label="Select" />
                  <th className="px-3 py-2">Time (UTC)</th>
                  <th className="px-3 py-2">Tenant</th>
                  <th className="px-3 py-2">HTTP</th>
                  <th className="px-3 py-2">Result</th>
                  <th className="px-3 py-2 min-w-[140px]">Response</th>
                  <th className="px-3 py-2">Payload preview</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map(row => (
                  <tr
                    key={row.id}
                    className={cn(
                      'hover:bg-border/20 transition-colors',
                      !row.success && selectedFailedIds.has(row.id) && 'bg-accent/5',
                    )}
                  >
                    <td className="px-3 py-2 align-top">
                      {!row.success ? (
                        <input
                          type="checkbox"
                          className="rounded border-border2"
                          checked={selectedFailedIds.has(row.id)}
                          onChange={() => toggleRow(row.id, row.success)}
                          aria-label={`Select failed attempt ${row.id}`}
                        />
                      ) : (
                        <span className="inline-block w-4" />
                      )}
                    </td>
                    <td className="px-3 py-2 align-top font-mono whitespace-nowrap">
                      <span className="text-ink block" title="Stored as UTC (same instant shown in local time below)">
                        {new Date(row.timestampUtc).toLocaleString('en-GB', {
                          timeZone: 'UTC',
                          dateStyle: 'short',
                          timeStyle: 'medium',
                        })}{' '}
                        <span className="text-muted text-[10px]">UTC</span>
                      </span>
                      <span className="text-[10px] text-muted block mt-0.5">
                        {new Date(row.timestampUtc).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' })}{' '}
                        (this device)
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <p className="font-semibold text-ink truncate max-w-[120px]" title={row.tenant}>{row.tenant}</p>
                      <p className="text-[10px] text-muted truncate max-w-[200px] mt-0.5" title={row.url}>{row.url}</p>
                    </td>
                    <td className="px-3 py-2 align-top font-mono text-muted">
                      {row.responseStatusCode ?? '—'}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <Badge variant={row.success ? 'green' : 'red'}>{row.success ? 'ok' : 'failed'}</Badge>
                    </td>
                    <td className="px-3 py-2 align-top text-muted font-mono break-all max-w-md">
                      {(() => {
                        const body = row.responseBody || (!row.success ? row.error : '')
                        if (!body) return '—'
                        const { summary, detail } = formatWebhookResponse(body)
                        return (
                          <div title={body}>
                            <p className="text-ink">{summary}</p>
                            {detail && (
                              <p className="text-[10px] text-muted mt-0.5">{detail}</p>
                            )}
                          </div>
                        )
                      })()}
                    </td>
                    <td className="px-3 py-2 align-top text-muted font-mono break-all max-w-lg text-[10px]">
                      {row.payloadPreview || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border bg-surface px-6 py-3 flex-shrink-0">
          <p className="text-[11px] text-muted font-mono">
            Showing <span className="text-ink">{from}–{to}</span> of <span className="text-ink">{totalCount}</span>
          </p>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce<(number | '...')[]>((acc, p, i, arr) => {
                if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push('...')
                acc.push(p)
                return acc
              }, [])
              .map((p, i) =>
                p === '...' ? (
                  <span key={`e${i}`} className="w-8 text-center text-xs text-muted">…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p as number)}
                    className={cn(
                      'h-8 min-w-8 px-2 rounded-md text-xs font-mono transition-colors',
                      page === p ? 'bg-accent text-white font-bold' : 'text-muted hover:text-ink hover:bg-border',
                    )}
                  >{p}</button>
                ))}
            <Button variant="ghost" size="icon" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
